import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import './ManagePoojaServices.css';

const SERVICE_FILTER_FIELDS = [
  { key: 'deity', label: 'Deity' },
  { key: 'tithis', label: 'Tithis' },
  { key: 'dosha', label: 'Dosha' },
  { key: 'benefits', label: 'Benefits' },
  { key: 'location', label: 'Location' }
];

const createEmptyServiceForm = () => ({
  serviceId: '',
  title: '',
  description: '',
  icon: 'fas fa-om',
  price: '',
  featured: false,
  isActive: true,
  availableTempleIds: [],
  filters: {
    deity: [],
    tithis: [],
    dosha: [],
    benefits: [],
    location: []
  }
});

const parseListInput = (value) => Array.from(new Set(
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
));

const formatListInput = (values = []) => (Array.isArray(values) ? values.join(', ') : '');

const buildServicePayload = (service) => ({
  serviceId: service.serviceId,
  title: service.title,
  description: service.description,
  icon: service.icon || 'fas fa-om',
  price: Number(service.price),
  isActive: !!service.isActive,
  featured: !!service.featured,
  availableTempleIds: service.availableTempleIds || [],
  filters: {
    deity: service.filters?.deity || [],
    tithis: service.filters?.tithis || [],
    dosha: service.filters?.dosha || [],
    benefits: service.filters?.benefits || [],
    location: service.filters?.location || []
  }
});

const ManagePoojaServices = () => {
  const [services, setServices] = useState([]);
  const [temples, setTemples] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingServiceId, setSavingServiceId] = useState(null);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  const [savingTempleId, setSavingTempleId] = useState(null);
  const [isCreatingTemple, setIsCreatingTemple] = useState(false);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [error, setError] = useState(null);
  const [serviceForm, setServiceForm] = useState(createEmptyServiceForm());
  const [templeForm, setTempleForm] = useState({
    templeId: '',
    name: '',
    location: '',
    isActive: true
  });

  const fetchCatalogue = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getPoojaAdminCatalogue();
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch pooja catalogue.');
      }

      setServices(response.data?.services || []);
      setTemples(response.data?.temples || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogue();
  }, []);

  const updateLocalService = (serviceId, updater) => {
    setServices((prev) => prev.map((service) => (
      service.serviceId === serviceId ? updater(service) : service
    )));
  };

  const handleServiceFieldChange = (serviceId, field, value) => {
    updateLocalService(serviceId, (service) => ({
      ...service,
      [field]: value
    }));
  };

  const handleServiceFilterChange = (serviceId, filterKey, value) => {
    updateLocalService(serviceId, (service) => ({
      ...service,
      filters: {
        ...(service.filters || {}),
        [filterKey]: parseListInput(value)
      }
    }));
  };

  const handleAvailabilityToggle = (serviceId, templeId) => {
    updateLocalService(serviceId, (service) => {
      const currentTempleIds = service.availableTempleIds || [];
      const isAvailable = currentTempleIds.includes(templeId);
      const availableTempleIds = isAvailable
        ? currentTempleIds.filter((id) => id !== templeId)
        : [...currentTempleIds, templeId];

      return {
        ...service,
        availableTempleIds
      };
    });
  };

  const handleSaveService = async (service) => {
    setSavingServiceId(service.serviceId);
    setError(null);

    try {
      const response = await apiService.updatePoojaService(service.serviceId, buildServicePayload(service));

      if (!response.success) {
        throw new Error(response.message || 'Failed to update pooja service.');
      }

      updateLocalService(service.serviceId, () => response.data);
      toast.success('Pooja service updated.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSavingServiceId(null);
    }
  };

  const handleServiceFormFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setServiceForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceFormFilterChange = (filterKey, value) => {
    setServiceForm((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterKey]: parseListInput(value)
      }
    }));
  };

  const handleServiceFormTempleToggle = (templeId) => {
    setServiceForm((prev) => {
      const isAvailable = prev.availableTempleIds.includes(templeId);
      return {
        ...prev,
        availableTempleIds: isAvailable
          ? prev.availableTempleIds.filter((id) => id !== templeId)
          : [...prev.availableTempleIds, templeId]
      };
    });
  };

  const handleCreateService = async (event) => {
    event.preventDefault();
    setIsCreatingService(true);
    setError(null);

    try {
      const response = await apiService.createPoojaService(buildServicePayload(serviceForm));
      if (!response.success) {
        throw new Error(response.message || 'Failed to create pooja service.');
      }

      setServiceForm(createEmptyServiceForm());
      await fetchCatalogue();
      toast.success('Pooja service added.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsCreatingService(false);
    }
  };

  const handleDeleteService = async (service) => {
    const confirmed = window.confirm(`Delete ${service.title}? Existing bookings keep their saved pooja title, but this service will no longer be bookable.`);
    if (!confirmed) return;

    setDeletingServiceId(service.serviceId);
    setError(null);

    try {
      const response = await apiService.deletePoojaService(service.serviceId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete pooja service.');
      }

      setServices((prev) => prev.filter((item) => item.serviceId !== service.serviceId));
      toast.success('Pooja service deleted.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setDeletingServiceId(null);
    }
  };

  const handleTempleActiveToggle = async (temple) => {
    setSavingTempleId(temple.templeId);
    setError(null);

    try {
      const response = await apiService.updatePoojaTemple(temple.templeId, {
        isActive: !temple.isActive
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update temple.');
      }

      setTemples((prev) => prev.map((item) => (
        item.templeId === temple.templeId ? response.data : item
      )));
      toast.success('Temple availability updated.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSavingTempleId(null);
    }
  };

  const handleTempleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setTempleForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetTempleForm = () => {
    setTempleForm({
      templeId: '',
      name: '',
      location: '',
      isActive: true
    });
  };

  const handleCreateTemple = async (event) => {
    event.preventDefault();
    setIsCreatingTemple(true);
    setError(null);

    try {
      const response = await apiService.createPoojaTemple(templeForm);
      if (!response.success) {
        throw new Error(response.message || 'Failed to create temple.');
      }

      resetTempleForm();
      await fetchCatalogue();
      toast.success('Temple added.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsCreatingTemple(false);
    }
  };

  const handleDeleteTemple = async (temple) => {
    const confirmed = window.confirm(`Delete ${temple.name}? This will remove it from all pooja availability lists.`);
    if (!confirmed) return;

    setSavingTempleId(temple.templeId);
    setError(null);

    try {
      const response = await apiService.deletePoojaTemple(temple.templeId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete temple.');
      }

      await fetchCatalogue();
      toast.success('Temple deleted.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSavingTempleId(null);
    }
  };

  if (isLoading) {
    return <div className="loading-spinner">Loading pooja catalogue...</div>;
  }

  return (
    <div className="manage-pooja-services-container">
      <div className="header-section">
        <h2>Temple Catalogue</h2>
        <button className="btn-primary-admin" onClick={fetchCatalogue}>
          <i className="fas fa-rotate"></i> Refresh
        </button>
      </div>

      {error ? <div className="error-message">Error: {error}</div> : null}

      <section className="pooja-admin-section">
        <h3>Add Ad Hoc Pooja Type</h3>
        <form className="pooja-service-create-form" onSubmit={handleCreateService}>
          <div className="pooja-service-form-grid">
            <div className="form-group">
              <label htmlFor="serviceId">Service ID</label>
              <input
                id="serviceId"
                name="serviceId"
                value={serviceForm.serviceId}
                onChange={handleServiceFormFieldChange}
                placeholder="e.g. satyanarayan-katha"
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceTitle">Title</label>
              <input
                id="serviceTitle"
                name="title"
                value={serviceForm.title}
                onChange={handleServiceFormFieldChange}
                placeholder="e.g. Satyanarayan Katha"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceIcon">Icon Class</label>
              <input
                id="serviceIcon"
                name="icon"
                value={serviceForm.icon}
                onChange={handleServiceFormFieldChange}
                placeholder="fas fa-om"
              />
            </div>

            <div className="form-group">
              <label htmlFor="servicePrice">Price</label>
              <input
                id="servicePrice"
                name="price"
                type="number"
                min="0"
                step="1"
                value={serviceForm.price}
                onChange={handleServiceFormFieldChange}
                required
              />
            </div>

            <div className="form-group pooja-service-description-field">
              <label htmlFor="serviceDescription">Description</label>
              <textarea
                id="serviceDescription"
                name="description"
                value={serviceForm.description}
                onChange={handleServiceFormFieldChange}
                placeholder="Brief description shown on Online Pooja page"
                rows="3"
                required
              />
            </div>
          </div>

          <div className="pooja-service-filter-grid">
            {SERVICE_FILTER_FIELDS.map((field) => (
              <label key={field.key}>
                {field.label}
                <input
                  value={formatListInput(serviceForm.filters[field.key])}
                  onChange={(event) => handleServiceFormFilterChange(field.key, event.target.value)}
                  placeholder="Comma separated"
                />
              </label>
            ))}
          </div>

          <div className="temple-availability-grid">
            {temples.map((temple) => (
              <label
                key={temple.templeId}
                className={`temple-availability-option ${!temple.isActive ? 'temple-inactive' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={serviceForm.availableTempleIds.includes(temple.templeId)}
                  disabled={!temple.isActive}
                  onChange={() => handleServiceFormTempleToggle(temple.templeId)}
                />
                <span>{temple.name}</span>
              </label>
            ))}
          </div>

          <div className="pooja-service-admin-actions">
            <div className="pooja-service-flags">
              <label className="pooja-admin-checkbox">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={!!serviceForm.isActive}
                  onChange={handleServiceFormFieldChange}
                />
                Active
              </label>

              <label className="pooja-admin-checkbox">
                <input
                  type="checkbox"
                  name="featured"
                  checked={!!serviceForm.featured}
                  onChange={handleServiceFormFieldChange}
                />
                Featured
              </label>
            </div>

            <button className="btn-primary-admin" type="submit" disabled={isCreatingService}>
              <i className="fas fa-plus"></i> {isCreatingService ? 'Adding...' : 'Add Pooja Type'}
            </button>
          </div>
        </form>
      </section>

      <section className="pooja-admin-section">
        <h3>Pooja Types, Prices And Temple Availability</h3>
        <div className="pooja-services-admin-list">
          {services.map((service) => (
            <div key={service.serviceId} className="pooja-service-admin-row">
              <div className="pooja-service-admin-main">
                <div className="pooja-service-title-block">
                  <small>{service.serviceId}</small>
                  <label>
                    Title
                    <input
                      value={service.title || ''}
                      onChange={(event) => handleServiceFieldChange(service.serviceId, 'title', event.target.value)}
                    />
                  </label>
                </div>

                <div className="pooja-service-admin-controls">
                  <label>
                    Icon
                    <input
                      value={service.icon || ''}
                      onChange={(event) => handleServiceFieldChange(service.serviceId, 'icon', event.target.value)}
                    />
                  </label>

                  <label>
                    Price
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={service.price}
                      onChange={(event) => handleServiceFieldChange(service.serviceId, 'price', event.target.value)}
                    />
                  </label>

                  <label className="pooja-admin-checkbox">
                    <input
                      type="checkbox"
                      checked={!!service.isActive}
                      onChange={(event) => handleServiceFieldChange(service.serviceId, 'isActive', event.target.checked)}
                    />
                    Active
                  </label>

                  <label className="pooja-admin-checkbox">
                    <input
                      type="checkbox"
                      checked={!!service.featured}
                      onChange={(event) => handleServiceFieldChange(service.serviceId, 'featured', event.target.checked)}
                    />
                    Featured
                  </label>
                </div>
              </div>

              <label className="pooja-service-description-edit">
                Description
                <textarea
                  value={service.description || ''}
                  onChange={(event) => handleServiceFieldChange(service.serviceId, 'description', event.target.value)}
                  rows="3"
                />
              </label>

              <div className="pooja-service-filter-grid">
                {SERVICE_FILTER_FIELDS.map((field) => (
                  <label key={field.key}>
                    {field.label}
                    <input
                      value={formatListInput(service.filters?.[field.key])}
                      onChange={(event) => handleServiceFilterChange(service.serviceId, field.key, event.target.value)}
                      placeholder="Comma separated"
                    />
                  </label>
                ))}
              </div>

              <div className="temple-availability-grid">
                {temples.map((temple) => {
                  const isAvailable = (service.availableTempleIds || []).includes(temple.templeId);
                  return (
                    <label
                      key={temple.templeId}
                      className={`temple-availability-option ${!temple.isActive ? 'temple-inactive' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isAvailable}
                        disabled={!temple.isActive}
                        onChange={() => handleAvailabilityToggle(service.serviceId, temple.templeId)}
                      />
                      <span>{temple.name}</span>
                    </label>
                  );
                })}
              </div>

              <div className="pooja-service-admin-actions">
                <span>{(service.availableTempleIds || []).length} temple(s) selected</span>
                <div className="pooja-service-action-buttons">
                  <button
                    className="btn-primary-admin"
                    disabled={savingServiceId === service.serviceId}
                    onClick={() => handleSaveService(service)}
                  >
                    {savingServiceId === service.serviceId ? 'Saving...' : 'Save Service'}
                  </button>
                  <button
                    className="action-btn btn-delete"
                    disabled={deletingServiceId === service.serviceId}
                    onClick={() => handleDeleteService(service)}
                  >
                    <i className="fas fa-trash"></i> {deletingServiceId === service.serviceId ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pooja-admin-section">
        <h3>Listed Temples</h3>
        <form className="temple-admin-form" onSubmit={handleCreateTemple}>
          <div className="form-group">
            <label htmlFor="templeId">Temple ID</label>
            <input
              id="templeId"
              name="templeId"
              value={templeForm.templeId}
              onChange={handleTempleFormChange}
              placeholder="e.g. kamakhya"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Temple Name</label>
            <input
              id="name"
              name="name"
              value={templeForm.name}
              onChange={handleTempleFormChange}
              placeholder="e.g. Kamakhya Temple, Guwahati"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              id="location"
              name="location"
              value={templeForm.location}
              onChange={handleTempleFormChange}
              placeholder="Optional"
            />
          </div>

          <label className="pooja-admin-checkbox temple-form-active">
            <input
              type="checkbox"
              name="isActive"
              checked={templeForm.isActive}
              onChange={handleTempleFormChange}
            />
            Active
          </label>

          <button className="btn-primary-admin" type="submit" disabled={isCreatingTemple}>
            <i className="fas fa-plus"></i> {isCreatingTemple ? 'Adding...' : 'Add Temple'}
          </button>
        </form>

        <div className="temples-admin-table-wrapper">
          <table className="temples-admin-table">
            <thead>
              <tr>
                <th>Temple</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {temples.map((temple) => (
                <tr key={temple.templeId}>
                  <td data-label="Temple">
                    <strong>{temple.name}</strong>
                    <small>{temple.templeId}</small>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge ${temple.isActive ? 'active' : 'inactive'}`}>
                      {temple.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Action">
                    <button
                      className="action-btn"
                      disabled={savingTempleId === temple.templeId}
                      onClick={() => handleTempleActiveToggle(temple)}
                    >
                      {savingTempleId === temple.templeId
                        ? 'Saving...'
                        : temple.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="action-btn btn-delete"
                      disabled={savingTempleId === temple.templeId}
                      onClick={() => handleDeleteTemple(temple)}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ManagePoojaServices;
